import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../components/context/AuthContext';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s]).{8,}$/;

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Extract params from URL
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const email = params.get('email');

  useEffect(() => {
    // Basic safety check: if no token or email, redirect away
    if (!token || !email) {
      setError('Invalid or expired reset link.');
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!PASSWORD_PATTERN.test(formData.newPassword)) {
      setError('Password must be 8+ characters with uppercase, lowercase, number, and symbol.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    const result = await resetPassword({
      email,
      token,
      newPassword: formData.newPassword
    });

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md relative">
        <div className="bg-card/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-10 border border-border overflow-hidden">
          
          {/* Logo / Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-primary to-secondary rounded-xl">
                <Icon name="Lock" size={16} color="white" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-foreground">Claw</h1>
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground">Set New Password</h2>
            <p className="text-sm text-muted-foreground font-caption mt-1">
              Please choose a strong password for <strong>{email}</strong>
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error font-caption">{error}</p>
            </div>
          )}

          {success ? (
            <div className="space-y-6 text-center animate-in fade-in zoom-in duration-300">
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-2">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground text-lg">Password Updated</h3>
                <p className="text-sm text-muted-foreground font-caption mt-2">
                  Your password has been reset successfully. You can now sign in with your new credentials.
                </p>
              </div>
              <Button onClick={() => navigate('/admin/auth')} className="w-full">
                Back to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2 font-heading">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                    <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
                  </div>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    required
                    className="pl-10 pr-10 placeholder:text-muted-foreground/[0.15]"
                    disabled={!token || !email}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2 font-heading">
                  Confirm New Password
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                    <Icon name="ShieldCheck" size={18} color="var(--color-muted-foreground)" />
                  </div>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    className="pl-10 pr-10 placeholder:text-muted-foreground/[0.15]"
                    disabled={!token || !email}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                variant="default" 
                className="w-full" 
                disabled={isLoading || !token || !email}
              >
                {isLoading ? (
                  <><Icon name="Loader" size={18} className="animate-spin mr-2" />Updating...</>
                ) : (
                  <><Icon name="RefreshCw" size={18} className="mr-2" />Reset Password</>
                )}
              </Button>
              
              <button
                type="button"
                onClick={() => navigate('/admin/auth')}
                className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors font-caption"
              >
                Back to Sign In
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
